import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ScrollText, Zap, ChevronRight, Star, Award } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useCopyScoring } from '../hooks/useCopyScoring';
import { THE_BRIEF_EXERCISES } from '../data/copywritingExercises';
import CopyEditor from '../components/CopyEditor';
import CopyScoreDisplay from '../components/CopyScoreDisplay';

const ACCENT = '#7c3aed';

const CopywritingBrief = () => {
  usePageTitle('The Brief | Castle Atlas');
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { scoreExercise, getFeatured, loading, error, clearError } = useCopyScoring();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  const [currentIdx, setCurrentIdx] = useState(0);
  const [userCopy, setUserCopy] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [showFeatured, setShowFeatured] = useState(false);

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (exerciseId) {
      const idx = THE_BRIEF_EXERCISES.findIndex(e => e.id === exerciseId);
      if (idx >= 0) setCurrentIdx(idx);
    }
  }, [exerciseId]);

  // Load featured submissions (module-wide, matching the existing hook contract)
  useEffect(() => {
    getFeatured().then(data => setFeatured(data.featured || []));
  }, [getFeatured]);

  const exercise = THE_BRIEF_EXERCISES[currentIdx];
  const isRevision = exercise?.mode === 'revision';

  const handleSubmit = useCallback(async () => {
    if (submitted || !userCopy.trim()) return;
    setSubmitted(true);

    try {
      const res = await scoreExercise({
        exerciseId: exercise.id,
        moduleId: 'brief',
        mode: exercise.mode,
        userText: userCopy.trim(),
      });
      setResult(res);
      setScore(res.scores?.overall ?? 0);
    } catch (err) {
      // unlock so the student can retry; the hook holds the message shown under the button
      console.error(err);
      setSubmitted(false);
    }
  }, [submitted, userCopy, exercise, scoreExercise]);

  const nextExercise = () => {
    if (currentIdx < THE_BRIEF_EXERCISES.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setUserCopy('');
      setSubmitted(false); setResult(null); setScore(null); clearError();
      navigate(`/skills/copywriting/brief/${THE_BRIEF_EXERCISES[currentIdx + 1].id}`, { replace: true });
    }
  };

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card: isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#78716c' : '#a8a29e',
    sub: isDark ? '#57534e' : '#b4aca6',
    draftBg: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
    draftBorder: isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.3)',
  };

  const AxisPill = ({ label, muted }) => (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99,
      background: muted ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'rgba(124,58,237,0.10)',
      color: muted ? c.muted : ACCENT, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{label}</span>
  );

  const isFeatured = result?.featured || score >= 85;

  if (!exercise) return null;

  return (
    <div style={{ maxWidth: '100%', paddingBottom: 80, background: c.pageBg, minHeight: '100vh' }}>
    <div className="rail-clearance">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);} }
        @keyframes shimmer { 0%{background-position:-200% 0;}100%{background-position:200% 0;} }
        .anim-fade { animation: fadeUp 0.3s ease-out both; }
        .featured-badge {
          background: linear-gradient(90deg, #7c3aed, #a78bfa, #7c3aed);
          background-size: 200% 100%;
          animation: shimmer 3s ease infinite;
        }
      `}</style>

      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: isDark ? 'rgba(14,13,12,0.85)' : 'rgba(250,249,247,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/skills/copywriting')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8,
        }}>
          <ArrowLeft size={18} color={c.head} />
        </button>
        <ScrollText size={16} color={ACCENT} />
        <span style={{ fontSize: 13, fontWeight: 800, color: c.head, flex: 1 }}>The Brief</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.muted }}>
          {currentIdx + 1} / {THE_BRIEF_EXERCISES.length}
        </span>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '16px 16px 40px' : '24px 24px 60px' }}>
        {/* Brief Card */}
        <div className="anim-fade" style={{
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: 20, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {exercise.format && <AxisPill label={exercise.format} muted />}
            <AxisPill label={exercise.primaryAxis} />
            {exercise.secondaryAxes?.map(a => <AxisPill key={a} label={a} muted />)}
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: c.head, marginBottom: 10 }}>{exercise.title}</h2>
          <p style={{
            fontSize: 13, color: c.sub, lineHeight: 1.7, whiteSpace: 'pre-wrap',
            ...(isRevision ? { background: c.draftBg, border: `1px solid ${c.draftBorder}`, borderRadius: 12, padding: '12px 14px' } : {}),
          }}>
            {exercise.briefText}
          </p>
        </div>

        {/* Editor */}
        <div className="anim-fade" style={{ marginBottom: 20 }}>
          <CopyEditor
            key={`${exercise.id}:copy`}
            draftKey={`${exercise.id}:copy`}
            value={userCopy}
            onChange={setUserCopy}
            placeholder="Write your copy here..."
            label="Your copy"
            rows={8}
            isDark={isDark}
            autoFocus
            disabled={submitted}
          />

          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={!userCopy.trim() || loading}
              style={{
                width: '100%', marginTop: 16, padding: '14px 24px', borderRadius: 12,
                background: (!userCopy.trim() || loading) ? (isDark ? '#2d2825' : '#e8e4df') : ACCENT,
                color: (!userCopy.trim() || loading) ? c.muted : '#fff',
                fontSize: 14, fontWeight: 700, border: 'none', cursor: (!userCopy.trim() || loading) ? 'not-allowed' : 'pointer',
                boxShadow: (!userCopy.trim() || loading) ? 'none' : `0 4px 20px -4px rgba(124,58,237,0.45)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Zap size={15} />
              {loading ? 'Scoring...' : isRevision ? 'Submit Fix' : 'Submit Copy'}
            </button>
          )}
          {error && !submitted && (
            <p role="alert" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: '#ef4444', lineHeight: 1.5 }}>
              {error} Your answer is still here; try again.
            </p>
          )}
        </div>

        {/* Results */}
        {submitted && result && (
          <div className="anim-fade">
            {isFeatured && (
              <div style={{
                marginBottom: 16, padding: '14px 18px', borderRadius: 14,
                display: 'flex', alignItems: 'center', gap: 10,
              }} className="featured-badge">
                <Award size={18} color="#fff" />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>Featured submission!</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Your copy scored 85+ and may be highlighted.</div>
                </div>
              </div>
            )}

            <CopyScoreDisplay
              score={score}
              isNew
              isDark={isDark}
              extraScores={result.scores}
              feedback={result.feedback}
            />

            {/* Featured submissions toggle */}
            {featured.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <button
                  onClick={() => setShowFeatured(!showFeatured)}
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: 12,
                    background: c.card, border: `1px solid ${c.border}`,
                    display: 'flex', alignItems: 'center', gap: 8,
                    cursor: 'pointer', color: c.head, fontSize: 13, fontWeight: 700,
                  }}
                >
                  <Star size={14} color={ACCENT} />
                  <span style={{ flex: 1 }}>See other high-scoring submissions for this brief</span>
                  <span style={{ fontSize: 11, color: c.muted }}>{featured.length}</span>
                </button>

                {showFeatured && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {featured.slice(0, 5).map((f, i) => (
                      <div key={i} style={{
                        padding: '12px 16px', borderRadius: 12,
                        background: c.card, border: `1px solid ${c.border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT }}>Score: {f.score}</span>
                          <span style={{ fontSize: 10, color: c.muted }}>{new Date(f.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {currentIdx < THE_BRIEF_EXERCISES.length - 1 && (
              <button
                onClick={nextExercise}
                style={{
                  width: '100%', marginTop: 20, padding: '14px 24px', borderRadius: 12,
                  background: ACCENT, color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                  boxShadow: `0 4px 20px -4px rgba(124,58,237,0.45)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                Next Exercise <ChevronRight size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default CopywritingBrief;
